import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuthStore } from '../store/authStore';
import { useFamilyStore } from '../store/familyStore';
import { useTodoStore } from '../store/todoStore';
import { useThemeStore } from '../store/themeStore';
import { getDesignTokens } from '../utils/designTokens';
import { CoreNavRail } from '../components/CoreNavRail';
import NavBar from '../components/NavBar';
import { formatDate } from '../utils/time';
import { parseTodoDueDate } from '../utils/todoDate';
import { Icon } from '../utils/icons';
import { TodoStatus, Todo, TodoPriority, TodoList } from '../types/todo';
import TodoModal from '../components/todos/TodoModal';
import ListModal from '../components/todos/ListModal';
import LoadingSpinner from '../components/LoadingSpinner';
import { useToastStore } from '../store/toastStore';

export default function TodosPage() {
  const navigate = useNavigate();
  const { isAuthenticated, idToken } = useAuthStore();
  const { family, fetchFamily } = useFamilyStore();
  const { todos, lists, fetchTodos, fetchLists, createTodo, updateTodo, deleteTodo, createList, updateList, deleteList } = useTodoStore();
  const { isDarkMode } = useThemeStore();
  const { showToast } = useToastStore();
  const tokens = getDesignTokens(isDarkMode);

  const [scope, setScope] = useState<'my' | 'family'>('family');
  const [statusFilter, setStatusFilter] = useState<TodoStatus | 'all'>('open');
  const [selectedList, setSelectedList] = useState<string | null>(null);
  const [titleInput, setTitleInput] = useState('');
  const [dueInput, setDueInput] = useState<string>('');
  const [priorityInput, setPriorityInput] = useState<'low' | 'medium' | 'high' | 'critical'>('medium');
  const [listNameInput, setListNameInput] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editingTodo, setEditingTodo] = useState<Todo | null>(null);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showListModal, setShowListModal] = useState(false);
  const [editingList, setEditingList] = useState<TodoList | null>(null);
  const [isSubmittingList, setIsSubmittingList] = useState(false);
  const [listToDelete, setListToDelete] = useState<string | null>(null);
  const [isQuickAdding, setIsQuickAdding] = useState(false);
  const [isListQuickAdding, setIsListQuickAdding] = useState(false);
  const [pendingStatusIds, setPendingStatusIds] = useState<Record<string, boolean>>({});
  const [deletingTodoId, setDeletingTodoId] = useState<string | null>(null);
  const [deletingListId, setDeletingListId] = useState<string | null>(null);
  const [sortColumn, setSortColumn] = useState<'title' | 'due' | 'list' | 'assignee' | 'status' | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  useEffect(() => {
    if (!isAuthenticated || !idToken) {
      navigate('/login', { replace: true });
    }
  }, [isAuthenticated, idToken, navigate]);

  useEffect(() => {
    if (!family) {
      fetchFamily().catch((error) => {
        console.error('Error fetching family:', error);
        showToast(error instanceof Error ? error.message : 'Failed to load family', 'error');
      });
    }
  }, [family, fetchFamily, showToast]);

  useEffect(() => {
    fetchLists().catch((error) => {
      console.error('Error fetching lists:', error);
      showToast(error instanceof Error ? error.message : 'Failed to load lists', 'error');
    });
  }, [fetchLists, showToast]);

  useEffect(() => {
    fetchTodos({
      scope,
      status: statusFilter === 'all' ? undefined : statusFilter,
      listId: selectedList || undefined,
    }).catch((error) => {
      console.error('Error fetching todos:', error);
      showToast(error instanceof Error ? error.message : 'Failed to load todos', 'error');
    });
  }, [fetchTodos, scope, statusFilter, selectedList, showToast]);

  const handleCreateTodo = () => {
    setEditingTodo(null);
    setShowModal(true);
  };

  const handleEditTodo = (todo: Todo) => {
    setEditingTodo(todo);
    setShowModal(true);
  };

  const handleModalSubmit = async (data: {
    title: string;
    description?: string;
    dueDateTime?: Date;
    listId?: string;
    priority?: TodoPriority;
    assigneeId?: string;
    assigneeName?: string;
    isFamily: boolean;
    eventId?: string;
    eventTitle?: string;
  }) => {
    setIsSubmitting(true);
    try {
      if (editingTodo) {
        await updateTodo(editingTodo.todoId, data);
        showToast('Todo updated', 'success');
      } else {
        await createTodo(data);
        showToast('Todo created', 'success');
      }
      setShowModal(false);
      setEditingTodo(null);
    } catch (error) {
      console.error('Error saving todo:', error);
      showToast(error instanceof Error ? error.message : 'Failed to save todo', 'error');
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleModalClose = () => {
    setShowModal(false);
    setEditingTodo(null);
  };

  const handleToggleStatus = (todoId: string, status: TodoStatus) => {
    const nextStatus = status === 'open' ? 'done' : 'open';
    setPendingStatusIds((state) => ({ ...state, [todoId]: true }));
    updateTodo(todoId, { status: nextStatus })
      .then(() => {
        showToast(nextStatus === 'done' ? 'Todo marked done' : 'Todo reopened', 'success');
      })
      .catch((error) => {
        console.error('Error updating todo status:', error);
        showToast(error instanceof Error ? error.message : 'Failed to update status', 'error');
      })
      .finally(() => {
        setPendingStatusIds((state) => {
          const next = { ...state };
          delete next[todoId];
          return next;
        });
      });
  };

  const handleSort = (column: 'title' | 'due' | 'list' | 'assignee' | 'status') => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      // Set new column and default to ascending
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const familyMembers = family?.members || [];

  // Get current user ID from token
  const getCurrentUserId = (): string | null => {
    try {
      if (!idToken) return null;
      const payload = JSON.parse(atob(idToken.split('.')[1]));
      return payload.sub;
    } catch {
      return null;
    }
  };

  const currentUserId = getCurrentUserId();
  const currentUser = currentUserId ? familyMembers.find(m => m.userId === currentUserId) : null;

  const handleQuickAdd = async () => {
    if (!titleInput.trim()) return;
    setIsQuickAdding(true);
    // For date-only input, create Date at UTC midnight to avoid timezone issues
    const dueDate = dueInput ? (() => {
      const [year, month, day] = dueInput.split('-').map(Number);
      return new Date(Date.UTC(year, month - 1, day));
    })() : null;
    try {
      await createTodo({
        title: titleInput,
        dueDateTime: dueDate,
        listId: selectedList || undefined,
        priority: priorityInput,
        assigneeId: currentUserId || undefined,
        // assigneeName is not sent - backend will derive it from assigneeId
        isFamily: !currentUserId,
      });
      setTitleInput('');
      setDueInput('');
      showToast('Todo added', 'success');
    } catch (error) {
      console.error('Error creating todo:', error);
      showToast(error instanceof Error ? error.message : 'Failed to add todo', 'error');
    } finally {
      setIsQuickAdding(false);
    }
  };

  const sortedTodos = useMemo(() => {
    const todosCopy = [...todos];
    
    if (!sortColumn) {
      // Default: sort by due date
      return todosCopy.sort((a, b) => {
        const aDate = parseTodoDueDate(a.dueDateTime);
        const bDate = parseTodoDueDate(b.dueDateTime);
        const aTime = aDate ? aDate.getTime() : Number.MAX_SAFE_INTEGER;
        const bTime = bDate ? bDate.getTime() : Number.MAX_SAFE_INTEGER;
        return aTime - bTime;
      });
    }

    return todosCopy.sort((a, b) => {
      let comparison = 0;

      switch (sortColumn) {
        case 'title':
          comparison = a.title.localeCompare(b.title);
          break;
        case 'due':
          const aDate = parseTodoDueDate(a.dueDateTime);
          const bDate = parseTodoDueDate(b.dueDateTime);
          const aTime = aDate ? aDate.getTime() : Number.MAX_SAFE_INTEGER;
          const bTime = bDate ? bDate.getTime() : Number.MAX_SAFE_INTEGER;
          comparison = aTime - bTime;
          break;
        case 'list':
          const aListName = lists.find((l) => l.listId === a.listId)?.name || '';
          const bListName = lists.find((l) => l.listId === b.listId)?.name || '';
          comparison = aListName.localeCompare(bListName);
          break;
        case 'assignee':
          const aAssignee = a.assigneeName || familyMembers.find((m) => m.userId === a.assigneeId)?.name || (a.isFamily ? 'Family' : 'Unassigned');
          const bAssignee = b.assigneeName || familyMembers.find((m) => m.userId === b.assigneeId)?.name || (b.isFamily ? 'Family' : 'Unassigned');
          comparison = aAssignee.localeCompare(bAssignee);
          break;
        case 'status':
          comparison = a.status.localeCompare(b.status);
          break;
      }

      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [todos, sortColumn, sortDirection, lists, familyMembers]);

  return (
    <div className={`h-screen ${tokens.bg.gradient} relative flex flex-col overflow-hidden pr-20 transition-colors duration-300`}>
      <NavBar className={`${tokens.card.bg} ${tokens.card.shadow} ${tokens.card.border} border`} containerClassName="px-3 py-2">
        <div className="flex items-center gap-3">
          <span className={`text-lg font-semibold ${tokens.text.primary} font-serif`}>To Dos</span>
          <div className="flex items-center gap-2">
            <button
              className={`px-3 py-2 rounded-full text-sm font-semibold border flex items-center gap-2 transition-colors ${
                scope === 'my' 
                  ? `${tokens.button.primary} border-transparent` 
                  : `${tokens.card.bg} ${tokens.text.primary} ${tokens.border.light}`
              }`}
              onClick={() => setScope('my')}
            >
              {currentUser && (
                <div className="relative flex-shrink-0">
                  <div
                    className="w-5 h-5 rounded-full flex items-center justify-center text-white text-xs font-bold"
                    style={{ backgroundColor: currentUser.color }}
                  >
                    {!currentUser.avatar && currentUser.name.charAt(0).toUpperCase()}
                  </div>
                  {currentUser.avatar && (
                    <img
                      src={currentUser.avatar}
                      alt={currentUser.name}
                      className="absolute inset-0 w-5 h-5 rounded-full object-cover border-2 border-white z-10"
                      style={{ borderColor: currentUser.color }}
                      onError={(e) => {
                        (e.target as HTMLImageElement).style.display = 'none';
                      }}
                    />
                  )}
                </div>
              )}
              <span>My To Dos</span>
            </button>
            <button
              className={`px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${
                scope === 'family' 
                  ? `${tokens.button.primary} border-transparent` 
                  : `${tokens.card.bg} ${tokens.text.primary} ${tokens.border.light}`
              }`}
              onClick={() => setScope('family')}
            >
              Family To Dos
            </button>
          </div>
        </div>
      </NavBar>

      <div className="flex-1 overflow-hidden flex flex-col pt-0 px-2 pb-2">
        <div className="flex flex-row gap-2 h-full">
          <div className={`w-64 ${tokens.card.bg} ${tokens.border.light} border rounded-landing ${tokens.card.shadow} p-3 flex flex-col`}>
            <div className="flex items-center justify-between mb-2">
              <h3 className={`text-sm font-semibold ${tokens.text.primary} font-serif`}>Lists</h3>
              <button
                className={`px-2 py-1 text-xs rounded-landing ${tokens.button.primary} active:scale-95 disabled:opacity-60 disabled:cursor-not-allowed`}
                onClick={async () => {
                  if (!listNameInput.trim() || isListQuickAdding) return;
                  setIsListQuickAdding(true);
                  try {
                    await createList({ name: listNameInput });
                    setListNameInput('');
                    showToast('List created', 'success');
                  } catch (error) {
                    console.error('Error creating list:', error);
                    showToast(error instanceof Error ? error.message : 'Failed to create list', 'error');
                  } finally {
                    setIsListQuickAdding(false);
                  }
                }}
                disabled={isListQuickAdding}
              >
                {isListQuickAdding ? 'Adding...' : 'Add'}
              </button>
            </div>
            <div className="flex gap-2 mb-3">
              <input
                className={`flex-1 ${tokens.border.light} border rounded-landing px-2 py-2 text-sm ${tokens.card.bg} ${tokens.text.primary}`}
                placeholder="New list name"
                value={listNameInput}
                onChange={(e) => setListNameInput(e.target.value)}
              />
            </div>
            <div className="flex-1 overflow-y-auto space-y-2 pr-1">
              <button
                className={`w-full text-left px-3 py-2 rounded-landing border transition-colors ${
                  selectedList === null 
                    ? `${tokens.button.secondary} ${tokens.border.accent} ${tokens.text.primary}` 
                    : `${tokens.card.bg} ${tokens.border.light} ${tokens.text.primary}`
                }`}
                onClick={() => setSelectedList(null)}
              >
                All To Dos
              </button>
              {lists.map((list) => (
                <div
                  key={list.listId}
                  className={`w-full px-3 py-2 rounded-landing border transition-colors ${
                    selectedList === list.listId 
                      ? `${tokens.button.secondary} ${tokens.border.accent} ${tokens.text.primary}` 
                      : `${tokens.card.bg} ${tokens.border.light} ${tokens.text.primary}`
                  }`}
                >
                  <div className="flex items-center justify-between gap-2">
                    <button
                      className="flex-1 text-left"
                      onClick={() => setSelectedList(list.listId)}
                    >
                      <div className="flex items-center justify-between">
                        <span className="text-sm font-semibold">{list.name}</span>
                        {list.theme && <span className={`text-xs ${tokens.text.muted}`}>{list.theme}</span>}
                      </div>
                    </button>
                    <div className="flex items-center gap-1">
                      <button
                        className={`p-1.5 rounded-landing ${tokens.card.bg} ${tokens.border.light} border ${tokens.text.primary} hover:opacity-80 active:scale-95 transition-all min-w-[32px] min-h-[32px] flex items-center justify-center`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setEditingList(list);
                          setShowListModal(true);
                        }}
                        aria-label="Edit list"
                        title="Edit list"
                      >
                        <Icon name="edit" size="sm" />
                      </button>
                      <button
                        className={`p-1.5 rounded-landing ${isDarkMode ? 'bg-red-900/30 border-red-700 text-red-300 hover:bg-red-900/50' : 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'} border active:scale-95 transition-colors min-w-[32px] min-h-[32px] flex items-center justify-center`}
                        onClick={(e) => {
                          e.stopPropagation();
                          setListToDelete(list.listId);
                        }}
                        aria-label="Delete list"
                        title="Delete list"
                      >
                        <Icon name="delete" size="sm" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>

          <div className={`flex-1 ${tokens.card.bg} ${tokens.border.light} border rounded-landing ${tokens.card.shadow} p-4 flex flex-col overflow-hidden`}>
            <div className="flex-1 overflow-auto flex flex-col gap-4">
              {/* Quick Add Form - Single Line */}
              <div className="flex items-center gap-3">
                <input
                  className={`flex-1 ${tokens.border.light} border rounded-landing px-3 py-2.5 text-sm ${tokens.card.bg} ${tokens.text.primary}`}
                  placeholder="Add a to do"
                  value={titleInput}
                  onChange={(e) => setTitleInput(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      handleQuickAdd();
                    }
                  }}
                />
                <input
                  type="date"
                  className={`${tokens.border.light} border rounded-landing px-3 py-2.5 text-sm ${tokens.card.bg} ${tokens.text.primary}`}
                  value={dueInput}
                  onChange={(e) => setDueInput(e.target.value)}
                  aria-label="Due date"
                />
                <select
                  className={`${tokens.border.light} border rounded-landing px-3 py-2.5 text-sm ${tokens.card.bg} ${tokens.text.primary}`}
                  value={priorityInput}
                  onChange={(e) => setPriorityInput(e.target.value as any)}
                >
                  <option value="low">Low</option>
                  <option value="medium">Medium</option>
                  <option value="high">High</option>
                  <option value="critical">Critical</option>
                </select>
                <button
                  className={`px-4 py-2.5 rounded-landing ${tokens.button.primary} font-semibold active:scale-95 min-w-[80px] transition-colors flex items-center justify-center gap-2 disabled:opacity-60 disabled:cursor-not-allowed`}
                  onClick={handleQuickAdd}
                  disabled={!titleInput.trim() || isQuickAdding}
                >
                  {isQuickAdding ? <LoadingSpinner size="sm" label="Adding todo" /> : 'Add'}
                </button>
              </div>

              <div className="flex items-center gap-2">
                <button
                  className={`px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    statusFilter === 'open' 
                      ? `${tokens.button.secondary} ${tokens.border.accent} ${tokens.text.primary}` 
                      : `${tokens.card.bg} ${tokens.border.light} ${tokens.text.primary}`
                  }`}
                  onClick={() => setStatusFilter('open')}
                >
                  Open
                </button>
                <button
                  className={`px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    statusFilter === 'done' 
                      ? `${tokens.button.secondary} ${tokens.border.accent} ${tokens.text.primary}` 
                      : `${tokens.card.bg} ${tokens.border.light} ${tokens.text.primary}`
                  }`}
                  onClick={() => setStatusFilter('done')}
                >
                  Done
                </button>
                <button
                  className={`px-3 py-2 rounded-full text-sm font-semibold border transition-colors ${
                    statusFilter === 'all' 
                      ? `${tokens.button.secondary} ${tokens.border.accent} ${tokens.text.primary}` 
                      : `${tokens.card.bg} ${tokens.border.light} ${tokens.text.primary}`
                  }`}
                  onClick={() => setStatusFilter('all')}
                >
                  All
                </button>
              </div>

              <div className={`rounded-landing ${tokens.border.light} border overflow-auto`}>
              <table className="min-w-full text-sm">
                <thead className={`${tokens.bg.secondary} ${tokens.text.primary} sticky top-0`}>
                  <tr>
                    <th 
                      className={`text-left px-4 py-3 cursor-pointer ${isDarkMode ? 'hover:bg-[rgba(255,255,255,0.05)]' : 'hover:bg-[rgba(0,0,0,0.02)]'} select-none transition-colors`}
                      onClick={() => handleSort('title')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Title</span>
                        {sortColumn === 'title' && (
                          <Icon 
                            name={sortDirection === 'asc' ? 'arrow-up' : 'arrow-down'} 
                            size="sm" 
                            className={tokens.text.muted}
                          />
                        )}
                      </div>
                    </th>
                    <th 
                      className={`text-left px-4 py-3 cursor-pointer ${isDarkMode ? 'hover:bg-[rgba(255,255,255,0.05)]' : 'hover:bg-[rgba(0,0,0,0.02)]'} select-none transition-colors`}
                      onClick={() => handleSort('due')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Due</span>
                        {sortColumn === 'due' && (
                          <Icon 
                            name={sortDirection === 'asc' ? 'arrow-up' : 'arrow-down'} 
                            size="sm" 
                            className={tokens.text.muted}
                          />
                        )}
                      </div>
                    </th>
                    <th 
                      className={`text-left px-4 py-3 cursor-pointer ${isDarkMode ? 'hover:bg-[rgba(255,255,255,0.05)]' : 'hover:bg-[rgba(0,0,0,0.02)]'} select-none transition-colors`}
                      onClick={() => handleSort('list')}
                    >
                      <div className="flex items-center gap-2">
                        <span>List</span>
                        {sortColumn === 'list' && (
                          <Icon 
                            name={sortDirection === 'asc' ? 'arrow-up' : 'arrow-down'} 
                            size="sm" 
                            className={tokens.text.muted}
                          />
                        )}
                      </div>
                    </th>
                    <th 
                      className={`text-left px-4 py-3 cursor-pointer ${isDarkMode ? 'hover:bg-[rgba(255,255,255,0.05)]' : 'hover:bg-[rgba(0,0,0,0.02)]'} select-none transition-colors`}
                      onClick={() => handleSort('assignee')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Assignee</span>
                        {sortColumn === 'assignee' && (
                          <Icon 
                            name={sortDirection === 'asc' ? 'arrow-up' : 'arrow-down'} 
                            size="sm" 
                            className={tokens.text.muted}
                          />
                        )}
                      </div>
                    </th>
                    <th 
                      className={`text-left px-4 py-3 cursor-pointer ${isDarkMode ? 'hover:bg-[rgba(255,255,255,0.05)]' : 'hover:bg-[rgba(0,0,0,0.02)]'} select-none transition-colors`}
                      onClick={() => handleSort('status')}
                    >
                      <div className="flex items-center gap-2">
                        <span>Status</span>
                        {sortColumn === 'status' && (
                          <Icon 
                            name={sortDirection === 'asc' ? 'arrow-up' : 'arrow-down'} 
                            size="sm" 
                            className={tokens.text.muted}
                          />
                        )}
                      </div>
                    </th>
                    <th className={`text-left px-4 py-3 ${tokens.text.primary}`}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {sortedTodos.map((todo) => {
                    // Get assignee color for indicator
                    const assigneeMember = todo.assigneeId
                      ? familyMembers.find((m) => m.userId === todo.assigneeId)
                      : null;
                    
                    // Debug logging for color matching
                    if (todo.assigneeId && !assigneeMember) {
                      console.log('Assignee not found in family members:', {
                        todoId: todo.todoId,
                        todoAssigneeId: todo.assigneeId,
                        todoAssigneeName: todo.assigneeName,
                        familyMemberIds: familyMembers.map(m => ({ userId: m.userId, name: m.name })),
                      });
                    }
                    
                    // If todo has assigneeId, try to use their color, otherwise fall back
                    // If no assigneeId, use family color (blue) for family todos, gray for unassigned
                    const assigneeColor = todo.assigneeId
                      ? (assigneeMember?.color || '#9ca3af') // If assignee exists but not found, use gray
                      : (todo.isFamily ? '#3b82f6' : '#9ca3af'); // No assignee: blue for family, gray otherwise
                    
                    return (
                    <tr 
                      key={todo.todoId} 
                      className={`border-t ${tokens.border.light} ${isDarkMode ? 'hover:bg-[rgba(255,255,255,0.02)]' : 'hover:bg-[rgba(0,0,0,0.01)]'} transition-colors`}
                      style={{
                        borderLeftWidth: '4px',
                        borderLeftColor: todo.status === 'done' ? '#9ca3af' : assigneeColor,
                      }}
                    >
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-3">
                          <button
                            className={`w-5 h-5 rounded-landing border-2 flex items-center justify-center transition-all hover:scale-110 active:scale-95 ${
                              todo.status === 'done' 
                                ? `${tokens.accent.primary} border-transparent ${tokens.card.shadow}` 
                                : `${tokens.card.bg} ${tokens.border.light} ${isDarkMode ? 'hover:border-[#60a5fa] hover:bg-[rgba(96,165,250,0.1)]' : 'hover:border-[#d9776f] hover:bg-[rgba(217,119,111,0.1)]'}`
                            }`}
                            aria-label="Toggle status"
                            onClick={() => handleToggleStatus(todo.todoId, todo.status)}
                            disabled={pendingStatusIds[todo.todoId]}
                          >
                            {pendingStatusIds[todo.todoId] ? (
                              <LoadingSpinner size="sm" label="Updating todo" />
                            ) : (
                              todo.status === 'done' && <Icon name="check" size="sm" className="text-white" />
                            )}
                          </button>
                          <div className={`text-base font-semibold ${
                            todo.status === 'done' 
                              ? `${tokens.text.muted} line-through` 
                              : tokens.text.primary
                          }`}>
                            {todo.title}
                          </div>
                        </div>
                        {todo.description && <div className={`text-xs ${tokens.text.muted} mt-1`}>{todo.description}</div>}
                        {todo.eventId && todo.eventTitle && (
                          <div className={`text-xs ${tokens.accent.primary} mt-1 flex items-center gap-1`}>
                            <Icon name="calendar" size="sm" />
                            <span>Linked to: {todo.eventTitle}</span>
                          </div>
                        )}
                      </td>
                      <td className={`px-4 py-3 text-sm ${tokens.text.primary}`}>
                        {todo.dueDateTime ? formatDate(parseTodoDueDate(todo.dueDateTime) || new Date(), 'short') : 'No due date'}
                      </td>
                      <td className={`px-4 py-3 text-sm ${tokens.text.primary}`}>
                        {lists.find((l) => l.listId === todo.listId)?.name || '—'}
                      </td>
                      <td className={`px-4 py-3 text-sm ${tokens.text.primary}`}>
                        {todo.assigneeName || familyMembers.find((m) => m.userId === todo.assigneeId)?.name || (todo.isFamily ? 'Family' : 'Unassigned')}
                      </td>
                      <td className="px-4 py-3">
                        <span
                          className={`px-2 py-1 rounded-full text-xs font-semibold ${
                            todo.status === 'done' 
                              ? `${tokens.accent.tertiary} ${tokens.text.inverse}` 
                              : `${tokens.accent.secondary} ${tokens.text.inverse}`
                          }`}
                        >
                          {todo.status === 'done' ? 'Done' : 'Open'}
                        </span>
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-2">
                          <button
                            className={`${tokens.button.secondary} ${tokens.border.accent} border flex items-center gap-1 px-3 py-2`}
                            onClick={() => handleEditTodo(todo)}
                          >
                            <Icon name="edit" size="sm" />
                            <span>Edit</span>
                          </button>
                          <button
                            className={`px-3 py-2 rounded-landing ${isDarkMode ? 'bg-red-900/30 border-red-700 text-red-300 hover:bg-red-900/50' : 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'} border active:scale-95 transition-colors`}
                            onClick={async () => {
                              if (deletingTodoId) return;
                              setDeletingTodoId(todo.todoId);
                              try {
                                await deleteTodo(todo.todoId);
                                showToast('Todo deleted', 'success');
                              } catch (error) {
                                console.error('Error deleting todo:', error);
                                showToast(error instanceof Error ? error.message : 'Failed to delete todo', 'error');
                              } finally {
                                setDeletingTodoId(null);
                              }
                            }}
                            disabled={deletingTodoId === todo.todoId}
                          >
                            {deletingTodoId === todo.todoId ? 'Deleting...' : 'Delete'}
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                  {sortedTodos.length === 0 && (
                    <tr>
                      <td colSpan={6} className="px-4 py-6 text-center text-gray-500">
                        No to dos yet. Add one to get started.
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
              </div>
            </div>
          </div>
        </div>
      </div>

      <CoreNavRail active="todos" />

      {/* Todo Modal */}
      <TodoModal
        isOpen={showModal}
        onClose={handleModalClose}
        onSubmit={handleModalSubmit}
        editingTodo={editingTodo}
        lists={lists}
        isLoading={isSubmitting}
      />

      {/* List Modal */}
      <ListModal
        isOpen={showListModal}
        onClose={() => {
          setShowListModal(false);
          setEditingList(null);
        }}
        onSubmit={async (data) => {
          setIsSubmittingList(true);
          try {
            if (editingList) {
              await updateList(editingList.listId, data);
              showToast('List updated', 'success');
            } else {
              await createList(data);
              showToast('List created', 'success');
            }
            setShowListModal(false);
            setEditingList(null);
          } catch (error) {
            console.error('Error saving list:', error);
            showToast(error instanceof Error ? error.message : 'Failed to save list', 'error');
          } finally {
            setIsSubmittingList(false);
          }
        }}
        editingList={editingList}
        isLoading={isSubmittingList}
      />

      {/* Delete Confirmation */}
      {listToDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
          <div
            className="absolute inset-0 bg-black bg-opacity-50"
            onClick={() => setListToDelete(null)}
          />
          <div className={`relative ${tokens.card.bg} rounded-landing ${tokens.card.shadow} max-w-md w-full p-6 z-10 ${tokens.border.light} border`}>
            <h3 className={`text-lg font-bold ${tokens.text.primary} mb-2`}>Delete List?</h3>
            <p className={`text-sm ${tokens.text.muted} mb-4`}>
              Are you sure you want to delete this list? Todos in this list will remain but will no longer be associated with a list.
            </p>
            <div className="flex gap-3">
              <button
                className={`flex-1 ${tokens.card.bg} ${tokens.border.light} border ${tokens.text.primary} font-semibold py-2.5 px-4 rounded-landing transition-colors hover:opacity-80`}
                onClick={() => setListToDelete(null)}
              >
                Cancel
              </button>
              <button
                className={`flex-1 ${isDarkMode ? 'bg-red-900/30 border-red-700 text-red-300 hover:bg-red-900/50' : 'bg-red-50 border-red-200 text-red-700 hover:bg-red-100'} border font-semibold py-2.5 px-4 rounded-landing transition-colors`}
                onClick={async () => {
                  if (listToDelete) {
                    try {
                      setDeletingListId(listToDelete);
                      await deleteList(listToDelete);
                      if (selectedList === listToDelete) {
                        setSelectedList(null);
                      }
                      showToast('List deleted', 'success');
                    } catch (error) {
                      console.error('Error deleting list:', error);
                      showToast(error instanceof Error ? error.message : 'Failed to delete list', 'error');
                    } finally {
                      setDeletingListId(null);
                    }
                  }
                  setListToDelete(null);
                }}
                disabled={deletingListId === listToDelete}
              >
                {deletingListId === listToDelete ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
